/**
 * Câmp opțional pentru data de expirare a unui credential. Valoarea internă e
 * `YYYY-MM-DD` (sau '' = fără expirare); spre server pleacă sfârșitul zilei
 * respective în fusul local, ca un credential să nu apară expirat în chiar
 * ziua aleasă.
 */
import { useTranslation } from 'react-i18next';

export function expiryInputFromIso(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function expiryInputToIso(value: string): string | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d, 23, 59, 59);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Cu câte zile înainte începe avertizarea de expirare. */
export const EXPIRY_WARNING_DAYS = 30;

export type ExpiryState = { kind: 'expired' } | { kind: 'soon'; days: number } | null;

/** `null` = fără dată de expirare sau mai mult de 30 de zile până la ea. */
export function expiryState(iso: string | null | undefined, now = Date.now()): ExpiryState {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  if (at < now) return { kind: 'expired' };
  const days = Math.floor((at - now) / 86_400_000);
  return days < EXPIRY_WARNING_DAYS ? { kind: 'soon', days } : null;
}

/** Etichetă mică lângă titlul unui credential care a expirat sau expiră curând. */
export function ExpiryBadge({ expiresAt }: { expiresAt: string | null | undefined }) {
  const { t } = useTranslation();
  const state = expiryState(expiresAt);
  if (!state) return null;
  const expired = state.kind === 'expired';
  const label = expired
    ? t('vault.expiry_expired')
    : state.days === 0
      ? t('vault.expiry_today')
      : t('vault.expiry_in', { count: state.days });
  return (
    <span
      className="badge text-[10px] ml-2"
      data-testid="expiry-badge"
      style={{
        color: expired ? 'var(--danger)' : 'var(--c-amber)',
        background: expired ? 'var(--danger-bg)' : 'var(--bg-amber)',
        border: `1px solid ${expired ? 'var(--danger)' : 'var(--c-amber)'}`,
      }}
    >
      {label}
    </span>
  );
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export default function ExpiryField({ value, onChange, disabled }: Props) {
  const { t } = useTranslation();
  return (
    <div>
      <label
        htmlFor="credential-expires"
        className="block text-xs font-medium text-[var(--text)] mb-1"
      >
        {t('vault.expires_label')}
      </label>
      <div className="flex items-center gap-2">
        <input
          id="credential-expires"
          type="date"
          className="input text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        {value && (
          <button
            type="button"
            className="text-xs text-[var(--text-muted)] hover:underline"
            onClick={() => onChange('')}
            disabled={disabled}
          >
            {t('vault.expires_clear')}
          </button>
        )}
      </div>
      <p className="text-[10px] text-[var(--text-dim)] mt-1">{t('vault.expires_hint')}</p>
    </div>
  );
}
